import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { getIndexerStatus, getNodeHealth, getKrownHoldersCount, getAllHolders, getDexMetrics, getDexMetricsByPool, getRecentDexSwaps, getEthPrice, getDexLiquidity, getAllTimeStats, getAthAtl, getLast24hStats, getPeriodStats, getDayStats, getSwapsByDay, getHourlyStats, getChartData, getDayChartData, getAllTransactions } from '../lib/api';
import { useWebSocket } from '../context/WebSocketContext';
import TokenPoolSimulator from '../components/TokenPoolSimulator';
import { LiquidityHistory } from '../components/LiquidityHistory';
import { DashboardHeader } from '../components/dashboard/DashboardHeader';
import { SectionNav } from '../components/dashboard/SectionNav';
import { MainStatsBanner } from '../components/dashboard/MainStatsBanner';
import { NodeHealthCard } from '../components/dashboard/NodeHealthCard';
import { PoolLiquiditySection } from '../components/dashboard/PoolLiquiditySection';
import { MarketAnalyticsSection } from '../components/dashboard/MarketAnalyticsSection';
import { NetworkActivitySection } from '../components/dashboard/NetworkActivitySection';
import { IndexerStatusCard } from '../components/dashboard/IndexerStatusCard';
import { HistoricalDayView } from '../components/dashboard/HistoricalDayView';
import { TooltipProvider } from '../components/ui/tooltip';
import { toast } from 'sonner';
// Constants for pagination
const HOLDERS_LIMIT = 50;

const normalizeAllTimeStats = (stats, athAtl = null) => {
  if (!stats) return null;
  const firstTime = stats.first_swap || stats.first_trade?.time || null;
  const lastTime = stats.last_swap || stats.current?.time || null;
  const hoursLive = firstTime && lastTime
    ? Math.max(0, (new Date(lastTime).getTime() - new Date(firstTime).getTime()) / (1000 * 60 * 60))
    : stats.hours_live;

  return {
    ...stats,
    ath: stats.ath || athAtl?.ath || null,
    atl: stats.atl || athAtl?.atl || null,
    totals: stats.totals || {
      volume_usd: stats.volume_usd ? `$${stats.volume_usd}` : '$0.00',
      swaps: stats.swap_count || 0,
      swap_txn_count: stats.swap_txn_count || 0,
      unique_traders: stats.unique_traders || 0,
    },
    first_trade: stats.first_trade || {
      time: firstTime,
      price_usd: null,
      market_cap: null,
    },
    current: stats.current || {
      time: lastTime,
      price_usd: null,
      market_cap: null,
    },
    hours_live: hoursLive,
    price_change_since_launch:
      typeof stats.price_change_since_launch === 'number' ? stats.price_change_since_launch : null,
  };
};

export default function Dashboard() {
  const navigate = useNavigate();
  const { priceData: wsPriceData, recentSwaps: wsRecentSwaps, autoUpdateEnabled } = useWebSocket();
  
  const [status, setStatus] = useState(null);
  const [nodeHealth, setNodeHealth] = useState(null);
  const [nodeHealthRefreshing, setNodeHealthRefreshing] = useState(false);
  const [krownHolders, setKrownHolders] = useState(0);
  const [dexMetrics, setDexMetrics] = useState(null);
  const [dexByPool, setDexByPool] = useState(null);
  const [ethPrice, setEthPrice] = useState(null);
  const [liquidity, setLiquidity] = useState(null);
  const [recentSwaps, setRecentSwaps] = useState([]);
  const [swapsTotal, setSwapsTotal] = useState(0);
  const [swapsOffset, setSwapsOffset] = useState(0);
  const [swapsLimit, setSwapsLimit] = useState(20);
  const [allHolders, setAllHolders] = useState({ holders: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [swapsLoading, setSwapsLoading] = useState(true);
  const [holdersLoading, setHoldersLoading] = useState(true);
  const [holdersOffset, setHoldersOffset] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  
  // Arbitrage calculator mode: 'buy-cheap' or 'sell-expensive'
  const [arbMode, setArbMode] = useState('buy-cheap');

  // Update state when WebSocket receives price data
  useEffect(() => {
    if (wsPriceData && wsPriceData.type === 'price_update') {
      // Update ETH price
      if (wsPriceData.eth_price_usd) {
        setEthPrice({ usd: wsPriceData.eth_price_usd, eth_price_usd: wsPriceData.eth_price_usd });
      }
      
      // Update liquidity data from WebSocket pools
      if (wsPriceData.pools && wsPriceData.pools.length > 0) {
        setLiquidity(prev => {
          if (!prev) return prev;
          
          // Map WebSocket pool data to existing liquidity structure
          const updatedPools = prev.pools.map(existingPool => {
            const wsPool = wsPriceData.pools.find(
              p => p.pair === existingPool.pair || 
                   (p.token0_symbol === existingPool.token0?.symbol && p.token1_symbol === existingPool.token1?.symbol)
            );
            
            if (wsPool) {
              return {
                ...existingPool,
                token0: { ...existingPool.token0, balance: wsPool.token0_balance },
                token1: { ...existingPool.token1, balance: wsPool.token1_balance },
                price: { ...existingPool.price, krown_usd: wsPool.krown_price_usd }
              };
            }
            return existingPool;
          });
          
          return {
            ...prev,
            pools: updatedPools,
            summary: {
              ...prev.summary,
              krown_price_usd_raw: wsPriceData.krown_price_usd,
              total_liquidity_usd: wsPriceData.total_liquidity_usd?.toFixed(2)
            }
          };
        });
      }
    }
  }, [wsPriceData]);
  
  // Update recent swaps from WebSocket when auto-update is enabled
  useEffect(() => {
    if (autoUpdateEnabled && wsRecentSwaps && wsRecentSwaps.length > 0) {
      // Merge WS swaps with existing swaps, avoiding duplicates
      setRecentSwaps(prev => {
        const existingHashes = new Set(prev.map(s => s.tx_hash));
        const newSwaps = wsRecentSwaps.filter(s => !existingHashes.has(s.tx_hash));
        if (newSwaps.length > 0) {
          // Prepend new swaps and keep only the latest based on swapsLimit
          return [...newSwaps, ...prev].slice(0, swapsLimit);
        }
        return prev;
      });
    }
  }, [wsRecentSwaps, autoUpdateEnabled, swapsLimit]);

  // Historical stats state
  const [allTimeStats, setAllTimeStats] = useState(null);
  const [last24hStats, setLast24hStats] = useState(null);
  const [periodStats, setPeriodStats] = useState(null);
  const [dayStats, setDayStats] = useState(null);
  const [historicalLoading, setHistoricalLoading] = useState(false);
  const [selectedPreset, setSelectedPreset] = useState('last-24h');
  const [selectedDate, setSelectedDate] = useState(null);
  const [calendarOpen, setCalendarOpen] = useState(false);
  
  // Historical day view mode
  const [historicalDayMode, setHistoricalDayMode] = useState(false);
  const [historicalDayData, setHistoricalDayData] = useState(null);
  const [historicalDaySwaps, setHistoricalDaySwaps] = useState([]);
  const [historicalDayChart, setHistoricalDayChart] = useState([]);

  // Hourly stats for time range selector
  const [hourlyStats, setHourlyStats] = useState(null);
  const [selectedHours, setSelectedHours] = useState(24);
  const [hourlyLoading, setHourlyLoading] = useState(false);
  
  // All transactions state
  const [allTransactions, setAllTransactions] = useState([]);
  const [transactionsTotal, setTransactionsTotal] = useState(0);
  const [transactionsOffset, setTransactionsOffset] = useState(0);
  const [transactionsLoading, setTransactionsLoading] = useState(false);
  const TRANSACTIONS_LIMIT = 50;
  
  // Chart data
  const [chartData, setChartData] = useState([]);
  const [chartLoading, setChartLoading] = useState(false);
  const [chartHeight, setChartHeight] = useState(350);

  // Main data fetch function
  const fetchAllData = useCallback(async () => {
    setRefreshing(true);
    // Broadcast refresh state to Layout header
    window.dispatchEvent(new CustomEvent('dashboard-refresh-state', { detail: { refreshing: true } }));
    try {
      // Keep dashboard usable even if one heavy endpoint is temporarily slow.
      // Production RPC/Atlas can occasionally reject concurrent all-time scans;
      // a single failure should not leave all cards in skeleton state.
      const results = await Promise.allSettled([
        getIndexerStatus(),              // 0
        getKrownHoldersCount(),          // 1
        getDexMetrics(),                 // 2
        getDexMetricsByPool(),           // 3
        getEthPrice(),                   // 4
        getDexLiquidity(),               // 5
        getRecentDexSwaps(swapsLimit, 0),// 6
        getLast24hStats(),               // 7
        getHourlyStats(24),              // 8
        getChartData(24),                // 9
        getNodeHealth()                  // 10
      ]);
      const getResult = (idx, fallback = null) => {
        if (results[idx]?.status === 'fulfilled') return results[idx].value;
        console.warn('Dashboard request failed', idx, results[idx]?.reason);
        return fallback;
      };

      const statusData = getResult(0);
      const holdersCountData = getResult(1, { total_holders: 0 });
      const metricsData = getResult(2);
      const poolData = getResult(3);
      const ethPriceData = getResult(4);
      const liquidityData = getResult(5);
      const swapsData = getResult(6, { swaps: [], total: 0 });
      const last24h = getResult(7);
      const hourly24 = getResult(8);
      const chart24 = getResult(9, { chart: [] });
      const nodeHealthData = getResult(10);

      if (statusData) setStatus(statusData);
      setKrownHolders(holdersCountData.total_holders || 0);
      if (metricsData) setDexMetrics(metricsData);
      if (poolData) setDexByPool(poolData);
      if (ethPriceData) setEthPrice(ethPriceData);
      if (liquidityData) setLiquidity(liquidityData);
      setRecentSwaps(swapsData.swaps || []);
      setSwapsTotal(swapsData.total || 0);
      setSwapsOffset(0);
      if (last24h) setLast24hStats(last24h);
      if (hourly24) setHourlyStats(hourly24);
      setChartData(chart24.chart || []);
      if (nodeHealthData) setNodeHealth(nodeHealthData);
      const failed = results.filter(r => r.status === 'rejected').length;
      if (failed) {
        toast.warning(`Dashboard loaded with ${failed} delayed data source${failed > 1 ? 's' : ''}`);
      } else {
        toast.success('Data refreshed');
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to refresh data');
    } finally {
      setLoading(false);
      setSwapsLoading(false);
      setRefreshing(false);
      // Broadcast refresh state to Layout header
      window.dispatchEvent(new CustomEvent('dashboard-refresh-state', { detail: { refreshing: false } }));
    }
  }, [swapsLimit]);

  const refreshNodeHealth = useCallback(async () => {
    setNodeHealthRefreshing(true);
    try {
      const data = await getNodeHealth();
      setNodeHealth(data);
      toast.success('Node health refreshed');
    } catch (error) {
      console.error('Failed to refresh node health:', error);
      toast.error('Failed to refresh node health');
    } finally {
      setNodeHealthRefreshing(false);
    }
  }, []);

  // Listen for refresh trigger from header
  useEffect(() => {
    const handleRefreshTrigger = () => fetchAllData();
    window.addEventListener('trigger-dashboard-refresh', handleRefreshTrigger);
    return () => window.removeEventListener('trigger-dashboard-refresh', handleRefreshTrigger);
  }, [fetchAllData]);

  // Fetch swaps when offset changes (pagination)
  const fetchSwaps = useCallback(async () => {
    setSwapsLoading(true);
    try {
      const swapsData = await getRecentDexSwaps(swapsLimit, swapsOffset);
      setRecentSwaps(swapsData.swaps || []);
      setSwapsTotal(swapsData.total || 0);
    } catch (error) {
      console.error('Failed to fetch swaps:', error);
    } finally {
      setSwapsLoading(false);
    }
  }, [swapsLimit, swapsOffset]);

  // Fetch all transactions
  const fetchTransactions = useCallback(async () => {
    setTransactionsLoading(true);
    try {
      const data = await getAllTransactions(TRANSACTIONS_LIMIT, transactionsOffset);
      setAllTransactions(data.transactions || []);
      setTransactionsTotal(data.total || 0);
    } catch (error) {
      console.error('Failed to fetch transactions:', error);
    } finally {
      setTransactionsLoading(false);
    }
  }, [transactionsOffset]);

  useEffect(() => {
    if (swapsOffset > 0) {
      fetchSwaps();
    }
  }, [swapsOffset, fetchSwaps]);

  useEffect(() => {
    fetchTransactions();
  }, [fetchTransactions]);

  useEffect(() => {
    fetchAllData();
  }, [fetchAllData]);

  useEffect(() => {
    const fetchHolders = async () => {
      setHoldersLoading(true);
      try {
        const data = await getAllHolders(HOLDERS_LIMIT, holdersOffset);
        setAllHolders(data);
      } catch (error) {
        console.error('Failed to fetch holders:', error);
      } finally {
        setHoldersLoading(false);
      }
    };
    fetchHolders();
  }, [holdersOffset]);

  // Fetch period stats with ATH/ATL
  const fetchPeriodStats = async (days) => {
    setHistoricalLoading(true);
    try {
      const now = new Date();
      const start = new Date(now.getTime() - days * 24 * 60 * 60 * 1000);
      const data = await getPeriodStats(start.toISOString());
      setPeriodStats(data);
      setDayStats(null);
    } catch (error) {
      console.error('Failed to fetch period stats:', error);
      toast.error('Failed to fetch period stats');
    } finally {
      setHistoricalLoading(false);
    }
  };

  // Fetch hourly stats for time range selector
  const fetchHourlyStats = async (hours) => {
    setHourlyLoading(true);
    setChartLoading(true);
    try {
      const [statsData, chartDataResult] = await Promise.all([
        getHourlyStats(hours),
        getChartData(hours)
      ]);
      setHourlyStats(statsData);
      setChartData(chartDataResult.chart || []);
    } catch (error) {
      console.error('Failed to fetch hourly stats:', error);
    } finally {
      setHourlyLoading(false);
      setChartLoading(false);
    }
  };

  // Handle time range selection
  const handleHoursSelect = async (hours) => {
    setSelectedHours(hours);
    await fetchHourlyStats(hours);
  };

  // Handle preset selection
  const handlePresetSelect = async (preset) => {
    setSelectedPreset(preset);
    setPeriodStats(null);
    setDayStats(null);
    setSelectedDate(null);
    
    switch (preset) {
      case 'last-24h':
        // Use the existing hourlyStats for last 24h (already fetched on load)
        setHistoricalLoading(true);
        try {
          const data = await getHourlyStats(24);
          setHourlyStats(data);
        } catch (error) {
          console.error('Failed to fetch 24h stats:', error);
        } finally {
          setHistoricalLoading(false);
        }
        break;
      case '7d':
        await fetchPeriodStats(7);
        break;
      case '30d':
        await fetchPeriodStats(30);
        break;
      case '90d':
        await fetchPeriodStats(90);
        break;
      case 'all-time':
        setHistoricalLoading(true);
        try {
          const [statsResult, athAtlResult] = await Promise.allSettled([
            getAllTimeStats(),
            getAthAtl(),
          ]);
          if (statsResult.status !== 'fulfilled') {
            throw statsResult.reason;
          }
          const athAtl = athAtlResult.status === 'fulfilled' ? athAtlResult.value : null;
          setAllTimeStats(normalizeAllTimeStats(statsResult.value, athAtl));
        } catch (error) {
          console.error('Failed to fetch all-time stats:', error);
          toast.error('Failed to fetch all-time stats');
        } finally {
          setHistoricalLoading(false);
        }
        break;
      default:
        break;
    }
  };

  // Handle date selection from calendar
  const handleDateSelect = async (date) => {
    if (!date) return;
    setSelectedDate(date);
    setCalendarOpen(false);
    setSelectedPreset('custom');
    setHistoricalLoading(true);
    try {
      const dateStr = format(date, 'yyyy-MM-dd');
      const data = await getDayStats(dateStr);
      setDayStats(data);
      setPeriodStats(null);
    } catch (error) {
      console.error('Failed to fetch day stats:', error);
      toast.error('No data for selected date');
    } finally {
      setHistoricalLoading(false);
    }
  };

  // Handle "See More" - show full historical dashboard
  const handleSeeMoreDay = async () => {
    if (!selectedDate) return;
    setHistoricalLoading(true);
    try {
      const dateStr = format(selectedDate, 'yyyy-MM-dd');
      const [stats, swaps, chartDataResult] = await Promise.all([
        getDayStats(dateStr),
        getSwapsByDay(dateStr, 100),
        getDayChartData(dateStr)
      ]);
      setHistoricalDayData(stats);
      setHistoricalDaySwaps(swaps.swaps || []);
      setHistoricalDayChart(chartDataResult.chart || []);
      setHistoricalDayMode(true);
    } catch (error) {
      console.error('Failed to fetch historical day data:', error);
      toast.error('Failed to load historical data');
    } finally {
      setHistoricalLoading(false);
    }
  };

  const exitHistoricalDayMode = () => {
    setHistoricalDayMode(false);
    setHistoricalDayData(null);
    setHistoricalDaySwaps([]);
    setHistoricalDayChart([]);
  };

  // V2-style AMM math (valid for full-range V3 liquidity)
  const getAmountOut = (amountIn, reserveIn, reserveOut, feePercent = 0.003) => {
    if (amountIn <= 0 || reserveIn <= 0 || reserveOut <= 0) return 0;
    const amountWithFee = amountIn * (1 - feePercent);
    return (amountWithFee * reserveOut) / (reserveIn + amountWithFee);
  };

  // Calculate arbitrage opportunity between pools
  // Supports two modes:
  // - 'buy-cheap': Buy KROWN in the cheaper pool to push its price UP
  // - 'sell-expensive': Sell KROWN in the expensive pool to push its price DOWN
  const calculateArbitrage = (pools, mode = 'buy-cheap') => {
    if (!pools || pools.length < 2) return null;
    
    const usdtPoolData = pools.find(p => p.token0.symbol === 'USDT' || p.token0.symbol === 'USDC');
    const ethPoolData = pools.find(p => p.token0.symbol === 'WETH' || p.token0.symbol === 'ETH');
    
    if (!usdtPoolData || !ethPoolData) return null;
    
    const usdtPrice = parseFloat(usdtPoolData.price.krown_usd);
    const ethPoolPrice = parseFloat(ethPoolData.price.krown_usd);
    const currentEthPrice = ethPrice?.eth_price_usd || ethPrice?.usd || 3500;
    
    if (!usdtPrice || !ethPoolPrice) return null;
    
    const priceDiffPercent = ((usdtPrice - ethPoolPrice) / ethPoolPrice) * 100;
    
    // Reserves - KROWN is token1
    const usdtReserve = parseFloat(usdtPoolData.token0.balance);
    const krownInUsdt = parseFloat(usdtPoolData.token1.balance);
    const ethReserve = parseFloat(ethPoolData.token0.balance);
    const krownInEth = parseFloat(ethPoolData.token1.balance);
    
    // Fee from pool data (3000 = 0.3%)
    const usdtFee = (usdtPoolData.fee || 3000) / 1000000;
    const ethFee = (ethPoolData.fee || 3000) / 1000000;
    
    // Determine which pool is cheaper/expensive
    const usdtIsCheaper = usdtPrice < ethPoolPrice;
    
    // Helper: simulate buy in a pool and return new price
    const simulateBuy = (amountUsd, reserve, krownReserve, fee, isEthPool) => {
      const amountIn = isEthPool ? amountUsd / currentEthPrice : amountUsd;
      const krownBought = getAmountOut(amountIn, reserve, krownReserve, fee);
      if (krownBought <= 0 || krownBought >= krownReserve * 0.5) return null;
      const newReserve = reserve + amountIn;
      const newKrown = krownReserve - krownBought;
      return isEthPool ? (newReserve * currentEthPrice) / newKrown : newReserve / newKrown;
    };
    
    // Helper: simulate sell in a pool and return new price
    const simulateSell = (amountUsd, reserve, krownReserve, fee, isEthPool, currentPrice) => {
      const krownToSell = amountUsd / currentPrice;
      const received = getAmountOut(krownToSell, krownReserve, reserve, fee);
      if (received <= 0 || received >= reserve * 0.5) return null;
      const newKrown = krownReserve + krownToSell;
      const newReserve = reserve - received;
      return isEthPool ? (newReserve * currentEthPrice) / newKrown : newReserve / newKrown;
    };
    
    let result = {
      mode,
      usdtIsCheaper,
      currentEthPrice,
      usdtPool: { price: usdtPrice, diffPercent: priceDiffPercent },
      ethPool: { price: ethPoolPrice, diffPercent: -priceDiffPercent }
    };
    
    if (mode === 'buy-cheap') {
      // Buy in cheap pool to push its price UP
      const targetPrice = usdtIsCheaper ? ethPoolPrice : usdtPrice;
      let low = 0, high = 500000, bestTrade = 0;
      
      for (let i = 0; i < 50; i++) {
        const mid = (low + high) / 2;
        const newPrice = usdtIsCheaper 
          ? simulateBuy(mid, usdtReserve, krownInUsdt, usdtFee, false)
          : simulateBuy(mid, ethReserve, krownInEth, ethFee, true);
        
        if (newPrice === null) { high = mid; continue; }
        const relDiff = Math.abs(newPrice - targetPrice) / targetPrice;
        if (relDiff < 0.0001) { bestTrade = mid; break; }
        if (newPrice < targetPrice) low = mid; else high = mid;
        bestTrade = mid;
      }
      
      result.usdtPool.action = usdtIsCheaper ? 'buy' : 'none';
      result.usdtPool.amountUsd = usdtIsCheaper ? bestTrade : 0;
      result.ethPool.action = !usdtIsCheaper ? 'buy' : 'none';
      result.ethPool.amountUsd = !usdtIsCheaper ? bestTrade : 0;
      result.ethPool.amountEth = !usdtIsCheaper ? bestTrade / currentEthPrice : 0;
      
    } else if (mode === 'sell-expensive') {
      // Sell in expensive pool to push its price DOWN
      const targetPrice = usdtIsCheaper ? usdtPrice : ethPoolPrice;
      let low = 0, high = 500000, bestTrade = 0;
      
      for (let i = 0; i < 50; i++) {
        const mid = (low + high) / 2;
        const newPrice = !usdtIsCheaper
          ? simulateSell(mid, usdtReserve, krownInUsdt, usdtFee, false, usdtPrice)
          : simulateSell(mid, ethReserve, krownInEth, ethFee, true, ethPoolPrice);
        
        if (newPrice === null) { high = mid; continue; }
        const relDiff = Math.abs(newPrice - targetPrice) / targetPrice;
        if (relDiff < 0.0001) { bestTrade = mid; break; }
        if (newPrice > targetPrice) low = mid; else high = mid;
        bestTrade = mid;
      }
      
      result.usdtPool.action = !usdtIsCheaper ? 'sell' : 'none';
      result.usdtPool.amountUsd = !usdtIsCheaper ? bestTrade : 0;
      result.ethPool.action = usdtIsCheaper ? 'sell' : 'none';
      result.ethPool.amountUsd = usdtIsCheaper ? bestTrade : 0;
      result.ethPool.amountEth = usdtIsCheaper ? bestTrade / currentEthPrice : 0;
    }
    
    return result;
  };

  const arbData = calculateArbitrage(liquidity?.pools, arbMode);

  const priceChange24h = liquidity?.summary?.price_change_24h?.change_percent ?? null;
  const priceChangeAvailable = liquidity?.summary?.price_change_24h?.data_available ?? false;

  const getSwapDirection = (swap) => {
    // In Uniswap V3 swap events:
    // - Negative amount = tokens leaving the pool (user RECEIVED them)
    // - Positive amount = tokens entering the pool (user SENT them)
    // 
    // For KROWN:
    // - Negative KROWN amount = user BOUGHT KROWN (received from pool)
    // - Positive KROWN amount = user SOLD KROWN (sent to pool)
    
    if (swap.token1_symbol === 'KROWN') {
      const amount1 = parseFloat(swap.amount1 || '0');
      return amount1 < 0 ? 'buy' : 'sell';  // Negative = bought, Positive = sold
    } else if (swap.token0_symbol === 'KROWN') {
      const amount0 = parseFloat(swap.amount0 || '0');
      return amount0 < 0 ? 'buy' : 'sell';  // Negative = bought, Positive = sold
    }
    return 'swap';
  };

  // Historical Day Mode View
  if (historicalDayMode && historicalDayData) {
    return (
      <TooltipProvider>
        <HistoricalDayView
          data={historicalDayData}
          chart={historicalDayChart}
          swaps={historicalDaySwaps}
          loading={historicalLoading}
          onExit={exitHistoricalDayMode}
          getSwapDirection={getSwapDirection}
        />
      </TooltipProvider>
    );
  }

  // Normal Dashboard View
  return (
    <TooltipProvider>
    <div className="space-y-8 animate-fade-in" data-testid="dashboard">
      {/* Header */}
      <DashboardHeader ethPrice={ethPrice} />

      {/* Section quick-nav (P5 — Chain Explorer Home) */}
      <SectionNav />

      {/* Main Stats Banner */}
      <div id="market"></div>

      {/* Main Stats Banner */}
      <MainStatsBanner
        liquidity={liquidity}
        dexMetrics={dexMetrics}
        krownHolders={krownHolders}
        priceChange24h={priceChange24h}
        priceChangeAvailable={priceChangeAvailable}
      />

      {/* Pool Liquidity Section */}
      <PoolLiquiditySection
        liquidity={liquidity}
        dexByPool={dexByPool}
        arbMode={arbMode}
        setArbMode={setArbMode}
        arbData={arbData}
      />

      {/* Market Analytics - Merged time range with stats and chart */}
      <MarketAnalyticsSection
        selectedHours={selectedHours}
        onHoursSelect={handleHoursSelect}
        hourlyLoading={hourlyLoading}
        hourlyStats={hourlyStats}
        chartData={chartData}
        chartHeight={chartHeight}
        setChartHeight={setChartHeight}
        chartLoading={chartLoading}
        selectedPreset={selectedPreset}
        onPresetSelect={handlePresetSelect}
        calendarOpen={calendarOpen}
        setCalendarOpen={setCalendarOpen}
        selectedDate={selectedDate}
        onDateSelect={handleDateSelect}
        dayStats={dayStats}
        onSeeMoreDay={handleSeeMoreDay}
        historicalLoading={historicalLoading}
        last24hStats={last24hStats}
        periodStats={periodStats}
        allTimeStats={allTimeStats}
      />

      {/* Network Activity */}
      <NetworkActivitySection
        autoUpdateEnabled={autoUpdateEnabled}
        swapsTotal={swapsTotal}
        transactionsTotal={transactionsTotal}
        allHolders={allHolders}
        swapsLimit={swapsLimit}
        setSwapsLimit={setSwapsLimit}
        setSwapsOffset={setSwapsOffset}
        swapsLoading={swapsLoading}
        recentSwaps={recentSwaps}
        getSwapDirection={getSwapDirection}
        swapsOffset={swapsOffset}
        transactionsLoading={transactionsLoading}
        allTransactions={allTransactions}
        transactionsOffset={transactionsOffset}
        setTransactionsOffset={setTransactionsOffset}
        transactionsLimit={TRANSACTIONS_LIMIT}
        holdersLoading={holdersLoading}
        holdersOffset={holdersOffset}
        setHoldersOffset={setHoldersOffset}
        holdersLimit={HOLDERS_LIMIT}
      />

      {/* Krown Node / Validator Health */}
      <NodeHealthCard
        nodeHealth={nodeHealth}
        nodeHealthRefreshing={nodeHealthRefreshing}
        onRefresh={refreshNodeHealth}
      />

      {/* Swap & Liquidity Simulator (dynamic pools — no hardcoding) */}
      <div className="mt-8">
        <TokenPoolSimulator chain="krown" token="0x9189d145d1d8e612c4c48bbd1af5a50cbee63d7d" />
      </div>

      {/* Pool Liquidity & Volume History */}
      <LiquidityHistory
        currentPrice={liquidity?.summary?.krown_price_usd_raw || 0}
      />

      {/* Indexer Status */}
      <IndexerStatusCard status={status} />
    </div>
    </TooltipProvider>
  );
}
